import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { msalInstance } from '../../auth/msalConfig';
import { isLang, type Lang, LANG_STORAGE_KEY, LANGS } from '../../i18n';
import { UserAvatar } from '../photo/UserAvatar';

interface UserMenuProps {
  oid: string;
  displayName: string;
  upn: string | null;
  role: string | null;
  isAdmin: boolean;
}

export function UserMenu({
  oid,
  displayName,
  upn,
  role,
  isAdmin,
}: UserMenuProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const raw = (i18n.resolvedLanguage ?? i18n.language).slice(0, 2);
  const current: Lang = isLang(raw) ? raw : 'fr';

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
      if (isLang(stored) && stored !== i18n.resolvedLanguage) {
        void i18n.changeLanguage(stored);
      }
    } catch {
      // localStorage may be unavailable — ignore.
    }
  }, [i18n]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        e.target instanceof Node &&
        !wrapperRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  function handleLang(code: Lang) {
    if (code === current) {
      return;
    }
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, code);
    } catch {
      // ignore
    }
    void i18n.changeLanguage(code);
  }

  function handleLogout() {
    setOpen(false);
    void msalInstance.logoutRedirect();
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="shrink-0 border-none bg-transparent p-0 transition-opacity hover:opacity-80"
        aria-haspopup="menu"
        aria-expanded={open}
        title={displayName}
      >
        <UserAvatar userId={oid} displayName={displayName} size="xs" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-72 border border-ink bg-card text-ink shadow-[4px_4px_0_var(--ink)]"
        >
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-b-rule p-4">
            <UserAvatar userId={oid} displayName={displayName} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{displayName}</p>
              {upn && <p className="mono truncate text-xs text-muted">{upn}</p>}
              <p className="label mt-1 flex items-center gap-1.5">
                <span className="size-1.5 bg-green" />
                {role ?? 'guest'}
              </p>
            </div>
          </div>

          {/* Links */}
          <ul className="border-b border-b-rule">
            {/* One page now: the profile and the photo are cards on it. */}
            <MenuLink
              to="/me"
              label={t('userMenu.profile', 'Mon profil')}
              onClick={() => setOpen(false)}
            />
            {isAdmin && (
              <MenuLink
                to="/admin/templates"
                label={t('nav.administration')}
                onClick={() => setOpen(false)}
              />
            )}
          </ul>

          {/* Lang */}
          <div className="flex items-center justify-between border-b border-b-rule px-4 py-3">
            <span className="label text-muted">
              {t('userMenu.language', 'Langue')}
            </span>
            <div className="flex items-center gap-2">
              {LANGS.map((code) => {
                const active = code === current;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => handleLang(code)}
                    aria-pressed={active}
                    className="label bg-transparent pb-px transition-colors"
                    style={{
                      color: active ? 'var(--ink)' : 'var(--muted)',
                      fontWeight: active ? 700 : 500,
                      borderBottom: active
                        ? '1px solid var(--ink)'
                        : '1px solid transparent',
                    }}
                  >
                    {code.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sign out */}
          <button
            type="button"
            onClick={handleLogout}
            className="w-full px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-(--bg-soft)"
            role="menuitem"
          >
            {t('common.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

function MenuLink({
  to,
  label,
  onClick,
}: {
  to: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <li>
      <Link
        to={to}
        onClick={onClick}
        className="flex items-center justify-between px-4 py-2.5 text-sm transition-colors hover:bg-(--bg-soft)"
        role="menuitem"
      >
        <span>{label}</span>
        <span className="text-muted" aria-hidden>
          →
        </span>
      </Link>
    </li>
  );
}
